import { User } from 'firebase/auth';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  increment,
  limit,
  orderBy,
  query,
  setDoc,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';

import {
  Atm,
  AtmListItem,
  AtmShareSettings,
  CreateAtmDto,
  SharedAtm,
  SharedAtmTransaction,
} from '../types/atm';
import { auth, firestore } from './firebase';

// Collection references
const ATMS_COLLECTION = 'atms';
const SHARED_ATMS_COLLECTION = 'sharedAtms';
const TRANSACTIONS_COLLECTION = 'atmTransactions';

/**
 * Creates a new ATM in Firestore
 * @param atmData The ATM data to create
 * @param userId The ID of the user creating the ATM
 * @returns The ID of the created ATM
 */
export const createAtm = async (atmData: CreateAtmDto, userId: string): Promise<string> => {
  try {
    console.log('🔥 FirebaseAtmService: Starting ATM creation for user:', userId);

    // Generate ID for options if not provided
    const options = atmData.options.map(option => ({
      ...option,
      id: option.id || uuidv4(),
    }));

    // Get user display name
    const user = auth.currentUser;
    const createdByName = user?.displayName || user?.email || 'Anonymous User';
    console.log('👤 FirebaseAtmService: Created by name:', createdByName);

    // Create ATM object
    const atm: Omit<Atm, 'id' | 'ref'> = {
      ...atmData,
      options,
      createdBy: userId,
      createdByName,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      transactionCount: 0,
      transactions: [],
      shareSettings: [],
    };

    console.log('📝 FirebaseAtmService: ATM object to save:', atm);

    // Add to Firestore
    console.log('☁️ FirebaseAtmService: Adding to Firestore...');
    const docRef = await addDoc(collection(firestore, ATMS_COLLECTION), atm);
    console.log('✅ FirebaseAtmService: ATM created successfully with ID:', docRef.id);

    return docRef.id;
  } catch (error) {
    console.error('Error creating ATM:', error);
    throw error;
  }
};

/**
 * Retrieves an ATM by ID
 * @param atmId The ID of the ATM to retrieve
 * @param includeAtmShareSettings Whether to include share settings (optional)
 * @returns The ATM data or null if not found
 */
export const getAtm = async (
  atmId: string,
  includeAtmShareSettings = false
): Promise<Atm | null> => {
  try {
    console.log('🔥 FirebaseAtmService: getAtm called for ID:', atmId);
    console.log('🔥 FirebaseAtmService: auth.currentUser:', auth.currentUser?.uid || 'null');

    const docRef = doc(firestore, ATMS_COLLECTION, atmId);
    console.log('📄 FirebaseAtmService: Document path:', `${ATMS_COLLECTION}/${atmId}`);

    const docSnap = await getDoc(docRef);
    console.log('📥 FirebaseAtmService: Document exists?', docSnap.exists());

    if (!docSnap.exists()) {
      console.log('❌ FirebaseAtmService: Document does not exist');
      return null;
    }

    console.log('✅ FirebaseAtmService: Document found, parsing data...');
    const atmData = docSnap.data() as Omit<Atm, 'id' | 'ref'>;

    const atm: Atm = {
      ...atmData,
      id: docSnap.id,
      ref: docRef,
    };

    console.log('🎯 FirebaseAtmService: Returning ATM with ID:', atm.id);
    return atm;
  } catch (error) {
    console.error('🚨 FirebaseAtmService: Error retrieving ATM:', error);
    if (error instanceof Error) {
      console.log('🚨 FirebaseAtmService: Error details:', error.message);
    }
    return null;
  }
};

/**
 * Updates an existing ATM
 * @param atmId The ID of the ATM to update
 * @param atmData The updated ATM data
 * @returns Promise that resolves when the update is complete
 */
export const updateAtm = async (atmId: string, atmData: Partial<Atm>): Promise<void> => {
  try {
    const docRef = doc(firestore, ATMS_COLLECTION, atmId);

    // Don't update these fields directly
    const { id, ref, createdBy, createdAt, transactionCount, shareSettings, ...updateData } =
      atmData;

    await updateDoc(docRef, {
      ...updateData,
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error('Error updating ATM:', error);
    throw error;
  }
};

/**
 * Deletes an ATM
 * @param atmId The ID of the ATM to delete
 * @returns Promise that resolves when the deletion is complete
 */
export const deleteAtm = async (atmId: string): Promise<void> => {
  try {
    const docRef = doc(firestore, ATMS_COLLECTION, atmId);
    await deleteDoc(docRef);

    // TODO: Consider whether to cascade delete shared ATMs and transactions
  } catch (error) {
    console.error('Error deleting ATM:', error);
    throw error;
  }
};

/**
 * Gets a list of ATMs created by a user
 * @param userId The ID of the user
 * @param limit The maximum number of ATMs to retrieve (optional)
 * @returns Array of ATM list items
 */
export const getUserAtms = async (userId: string, limitCount = 100): Promise<AtmListItem[]> => {
  try {
    console.log('🔥 FirebaseAtmService: getUserAtms called for userId:', userId);
    console.log('🔥 FirebaseAtmService: Limit count:', limitCount);

    const q = query(
      collection(firestore, ATMS_COLLECTION),
      where('createdBy', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    console.log('🔥 FirebaseAtmService: Executing query...');
    const querySnapshot = await getDocs(q);
    console.log('🔥 FirebaseAtmService: Query returned', querySnapshot.docs.length, 'documents');

    const atms = querySnapshot.docs.map(doc => {
      const data = doc.data();
      console.log('🔥 FirebaseAtmService: ATM doc:', doc.id, 'name:', data.name);
      return {
        id: doc.id,
        name: data.name,
        description: data.description,
        createdBy: data.createdBy,
        createdByName: data.createdByName,
        createdAt: data.createdAt,
        theme: data.theme,
        isPublic: data.isPublic,
        transactionCount: data.transactionCount,
      };
    });

    console.log('🔥 FirebaseAtmService: Returning', atms.length, 'ATMs');
    return atms;
  } catch (error) {
    console.error('💥 FirebaseAtmService: Error getting user ATMs:', error);
    throw error;
  }
};

/**
 * Gets a list of public ATMs
 * @param limitCount The maximum number of ATMs to retrieve
 * @returns Array of ATM list items
 */
export const getPublicAtms = async (limitCount = 20): Promise<AtmListItem[]> => {
  try {
    const q = query(
      collection(firestore, ATMS_COLLECTION),
      where('isPublic', '==', true),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        name: data.name,
        description: data.description,
        createdBy: data.createdBy,
        createdByName: data.createdByName,
        createdAt: data.createdAt,
        theme: data.theme,
        isPublic: data.isPublic,
        transactionCount: data.transactionCount,
      };
    });
  } catch (error) {
    console.error('Error getting public ATMs:', error);
    throw error;
  }
};

/**
 * Creates a shared version of an ATM
 * @param atmId The ID of the ATM to share
 * @param settings Share settings
 * @returns The share ID
 */
export const shareAtm = async (atmId: string, settings: AtmShareSettings): Promise<string> => {
  try {
    // Get current user
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User must be authenticated to share ATMs');
    }

    // Get the ATM
    const atm = await getAtm(atmId);

    if (!atm) {
      throw new Error('ATM not found');
    }

    // Create shared ATM document with full ATM data
    const shareId = Date.now().toString();
    const sharedAtm: SharedAtm = {
      shareId,
      atmId: atmId,
      atmName: atm.name,
      description: atm.description,
      ownerId: user.uid,
      ownerName: user.displayName || user.email || 'Anonymous',
      options: atm.options, // Include full ATM options
      theme: atm.theme, // Include theme
      atmSettings: atm.atmSettings, // Include settings
      shareSettings: settings,
      transactionCount: 0,
      transactions: [],
      isActive: true,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };

    // Save to Firestore
    const sharedDocRef = doc(firestore, SHARED_ATMS_COLLECTION, shareId);
    await setDoc(sharedDocRef, sharedAtm);

    // Update the parent ATM with this share setting
    const atmDocRef = doc(firestore, ATMS_COLLECTION, atmId);
    await updateDoc(atmDocRef, {
      shareSettings: [...(atm.shareSettings || []), settings],
      updatedAt: Timestamp.now(),
    });

    return shareId;
  } catch (error) {
    console.error('Error sharing ATM:', error);
    throw error;
  }
};

/**
 * Creates a shared ATM document directly in Firestore
 * @param sharedAtm The shared ATM data
 * @returns Promise that resolves when the shared ATM is created
 */
export const createSharedAtm = async (sharedAtm: SharedAtm): Promise<void> => {
  try {
    console.log('🔥 FirebaseAtmService: Creating shared ATM with ID:', sharedAtm.shareId);

    // Convert dates to Firestore Timestamps
    const firestoreSharedAtm = {
      ...sharedAtm,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      shareSettings: {
        ...sharedAtm.shareSettings,
        expiresAt: sharedAtm.shareSettings.expiresAt
          ? Timestamp.fromDate(new Date(sharedAtm.shareSettings.expiresAt as any))
          : null,
      },
    };

    // Save to Firestore
    const sharedDocRef = doc(firestore, SHARED_ATMS_COLLECTION, sharedAtm.shareId);
    await setDoc(sharedDocRef, firestoreSharedAtm);

    console.log('✅ FirebaseAtmService: Shared ATM created successfully');
  } catch (error) {
    console.error('❌ FirebaseAtmService: Error creating shared ATM:', error);
    throw error;
  }
};

/**
 * Gets a shared ATM by share ID
 * @param shareId The share ID
 * @returns The shared ATM or null if not found
 */
export const getSharedAtm = async (shareId: string): Promise<SharedAtm | null> => {
  try {
    const docRef = doc(firestore, SHARED_ATMS_COLLECTION, shareId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    return docSnap.data() as SharedAtm;
  } catch (error) {
    console.error('Error retrieving shared ATM:', error);
    throw error;
  }
};

/**
 * Processes a transaction on a shared ATM
 * @param shareId The share ID
 * @param transaction The transaction data
 * @returns Promise that resolves when the transaction is processed
 */
export const processSharedAtmTransaction = async (
  shareId: string,
  transaction: SharedAtmTransaction
): Promise<void> => {
  try {
    // Get the shared ATM
    const sharedDocRef = doc(firestore, SHARED_ATMS_COLLECTION, shareId);
    const sharedDocSnap = await getDoc(sharedDocRef);

    if (!sharedDocSnap.exists()) {
      throw new Error('Shared ATM not found');
    }

    const sharedAtm = sharedDocSnap.data() as SharedAtm;

    // Check if transactions are allowed
    if (!sharedAtm.isActive) {
      throw new Error('This ATM is no longer active');
    }

    if (
      sharedAtm.shareSettings.maxTransactions !== null &&
      sharedAtm.transactionCount >= (sharedAtm.shareSettings.maxTransactions || 0)
    ) {
      throw new Error('Maximum transactions reached for this ATM');
    }

    if (
      sharedAtm.shareSettings.expiresAt &&
      (sharedAtm.shareSettings.expiresAt as any).toDate() < new Date()
    ) {
      throw new Error('This shared ATM has expired');
    }

    // Clean transaction data - remove undefined fields as Firestore doesn't accept them
    const cleanTransaction: any = {
      id: transaction.id,
      shareId: transaction.shareId,
      optionText: transaction.optionText,
      type: transaction.type,
      timestamp: transaction.timestamp,
      userId: transaction.userId,
      status: transaction.status,
    };

    // Only add optional fields if they have values
    if (transaction.optionId !== undefined) cleanTransaction.optionId = transaction.optionId;
    if (transaction.amount !== undefined) cleanTransaction.amount = transaction.amount;
    if (transaction.message !== undefined) cleanTransaction.message = transaction.message;

    // Update the shared ATM with the new transaction
    await updateDoc(sharedDocRef, {
      transactions: [...sharedAtm.transactions, cleanTransaction],
      transactionCount: increment(1),
      updatedAt: Timestamp.now(),
    });

    // Update the parent ATM transaction count
    const atmDocRef = doc(firestore, ATMS_COLLECTION, sharedAtm.atmId);
    await updateDoc(atmDocRef, {
      transactionCount: increment(1),
    });
  } catch (error) {
    console.error('Error processing transaction:', error);
    throw error;
  }
};

/**
 * Gets all shares for an ATM
 * @param atmId The ID of the ATM
 * @returns Array of shared ATMs
 */
export const getAtmShares = async (atmId: string): Promise<SharedAtm[]> => {
  try {
    console.log('🔥 FirebaseAtmService: Getting shares for ATM:', atmId);

    // Try with orderBy first (requires index)
    try {
      const q = query(
        collection(firestore, SHARED_ATMS_COLLECTION),
        where('atmId', '==', atmId),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      console.log('🔥 FirebaseAtmService: Found', querySnapshot.docs.length, 'shares (with index)');

      const shares = querySnapshot.docs.map(doc => {
        const data = doc.data() as SharedAtm;
        return {
          ...data,
          shareId: doc.id,
        };
      });

      return shares;
    } catch (indexError: any) {
      // If index is still building, fall back to query without orderBy
      if (indexError?.message?.includes('index')) {
        console.log('⚠️ FirebaseAtmService: Index still building, querying without orderBy...');

        const q = query(
          collection(firestore, SHARED_ATMS_COLLECTION),
          where('atmId', '==', atmId)
        );

        const querySnapshot = await getDocs(q);
        console.log('🔥 FirebaseAtmService: Found', querySnapshot.docs.length, 'shares (without index)');

        const shares = querySnapshot.docs.map(doc => {
          const data = doc.data() as SharedAtm;
          return {
            ...data,
            shareId: doc.id,
          };
        });

        // Sort manually on the client side
        return shares.sort((a, b) => {
          const aTime = (a.createdAt as any)?.toDate?.() || new Date(a.createdAt as any);
          const bTime = (b.createdAt as any)?.toDate?.() || new Date(b.createdAt as any);
          return bTime.getTime() - aTime.getTime();
        });
      }

      throw indexError;
    }
  } catch (error) {
    console.error('💥 FirebaseAtmService: Error getting ATM shares:', error);
    throw error;
  }
};

/**
 * Updates the status of a shared ATM (activate/deactivate)
 * @param shareId The share ID
 * @param isActive Whether the share should be active
 * @returns Promise that resolves when the update is complete
 */
export const updateSharedAtmStatus = async (shareId: string, isActive: boolean): Promise<void> => {
  try {
    console.log('🔥 FirebaseAtmService: Updating shared ATM status:', shareId, 'to', isActive);

    const sharedDocRef = doc(firestore, SHARED_ATMS_COLLECTION, shareId);
    await updateDoc(sharedDocRef, {
      isActive,
      updatedAt: Timestamp.now(),
    });

    console.log('✅ FirebaseAtmService: Shared ATM status updated successfully');
  } catch (error) {
    console.error('❌ FirebaseAtmService: Error updating shared ATM status:', error);
    throw error;
  }
};

/**
 * Deletes a shared ATM
 * @param shareId The share ID
 * @returns Promise that resolves when the deletion is complete
 */
export const deleteSharedAtm = async (shareId: string): Promise<void> => {
  try {
    console.log('🔥 FirebaseAtmService: Deleting shared ATM:', shareId);

    const sharedDocRef = doc(firestore, SHARED_ATMS_COLLECTION, shareId);
    await deleteDoc(sharedDocRef);

    console.log('✅ FirebaseAtmService: Shared ATM deleted successfully');
  } catch (error) {
    console.error('❌ FirebaseAtmService: Error deleting shared ATM:', error);
    throw error;
  }
};
